/**
 * Ce que l'application est en train d'attendre, et où elle en est.
 *
 * Hedi, le 25 septembre 2026 : *« Quand ça charge, ça affiche : "Un instant…"
 * mais ça n'a pas l'air d'avancer alors que si. Je veux un loader qui avance
 * quand le vrai chargement avance. C'est vrai pour tout le site. »*
 *
 * Il faut donc dire honnêtement ce qui est **mesuré** et ce qui ne l'est pas,
 * parce que les deux existent ici et qu'un pixel ne les distingue pas :
 *
 * - **Mesuré, et c'est le plus clair** : le nombre de requêtes terminées sur le
 *   nombre lancées. Un écran qui en fait quatre avance quatre fois, pour de
 *   bon. Et la fin d'une requête est un fait, jamais une estimation.
 * - **Mesuré aussi, à un seul endroit** : l'envoi d'une photo vers le stockage,
 *   où les octets partis sur le réseau se comptent (`progresser`). C'est la
 *   seule opération de l'application dont l'avancement réel soit observable.
 * - **Prédit, partout ailleurs** : entre l'envoi d'une requête et la réponse du
 *   serveur, le navigateur ne sait **rien**. Le segment avance alors contre la
 *   **durée médiane des passages précédents de cette même opération**, retenue
 *   d'un soir sur l'autre. C'est une donnée réelle, mais c'est une prévision,
 *   et la forme du calcul le dit : la courbe est asymptotique et **ne touche
 *   jamais le bout**. Seule l'arrivée de la réponse termine un segment.
 *
 * C'est ce dernier point qui fait la différence avec une animation : une barre
 * qui glisse toujours pareil ne porte aucune information, et c'est exactement
 * ce qu'on reproche à « Un instant… ». Ici, une opération habituellement rapide
 * qui traîne se voit ralentir, et une opération longue — le chat de validation,
 * qui appelle un modèle — ne fait pas croire qu'elle est finie au bout d'une
 * seconde.
 *
 * L'enregistrement se fait dans `call()`, le point de passage unique vers
 * l'API — d'où ce fichier ici, à côté de lui, et non dans `ui/` : ce qui est
 * mesuré est un appel, et les écrans en sont les lecteurs. C'est la seule façon que « tout le site » soit vrai
 * sans réécrire trente-deux écrans, et la seule façon qu'un écran écrit demain
 * en profite sans y penser.
 */

import { useSyncExternalStore } from "react";

/** Ce qu'on suppose quand on n'a encore jamais chronométré l'opération. */
const REFERENCE_PAR_DEFAUT = 900;

/** Une référence absurde ne doit pas figer la barre ni la faire sauter. */
const REFERENCE_MIN = 150;
const REFERENCE_MAX = 60_000;

/**
 * Le plafond d'un segment en vol.
 *
 * Une requête en cours ne remplit jamais sa part : le remplir serait annoncer
 * une fin qu'on ne connaît pas, et c'est précisément le mensonge qu'on évite.
 */
const PLAFOND = 0.92;

/** Les durées retenues par opération, et combien. La médiane de cinq encaisse
 *  un passage aberrant — une coupure réseau de trente secondes — sans que la
 *  référence du lendemain en garde la trace. */
const MEMOIRE = "tuteur.chargement.durees";
const GARDEES = 5;

/** Le temps que la barre reste pleine avant de disparaître. Sans ce délai, la
 *  fin n'est jamais vue : la dernière image montrée serait un état
 *  intermédiaire, ce qui se lit comme un chargement interrompu. */
const TENUE_PLEINE = 220;

type EnVol = {
  id: number;
  operation: string;
  debut: number;
  reference: number;
  /** Renseigné seulement là où l'avancement se mesure vraiment. */
  mesure: number | null;
};

let enVol: EnVol[] = [];
/** Terminées depuis le début de la salve en cours. Remise à zéro quand plus
 *  rien n'est en vol : une salve est une attente, pas la session entière. */
let terminees = 0;
/** La valeur publiée. `useSyncExternalStore` exige qu'elle soit stable entre
 *  deux notifications — la recalculer à chaque lecture ferait boucler React. */
let valeur = 0;
let suivant = 1;
let minuterie: ReturnType<typeof setInterval> | null = null;
let effacement: ReturnType<typeof setTimeout> | null = null;

const abonnes = new Set<() => void>();

function maintenant(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function prevenir(): void {
  for (const abonne of abonnes) abonne();
}

/* --- Les durées déjà observées -------------------------------------------- */

function historique(): Record<string, number[]> {
  try {
    const brut = window.localStorage.getItem(MEMOIRE);
    if (!brut) return {};
    const lu: unknown = JSON.parse(brut);
    if (!lu || typeof lu !== "object" || Array.isArray(lu)) return {};
    return lu as Record<string, number[]>;
  } catch {
    /* un stockage refusé ou un contenu illisible ramène au comportement du
       premier soir : la référence par défaut. Jamais une exception — la barre
       ne doit pas pouvoir empêcher un écran de s'afficher. */
    return {};
  }
}

function retenir(operation: string, duree: number): void {
  if (!Number.isFinite(duree) || duree <= 0) return;
  try {
    const table = historique();
    const gardees = [...(table[operation] ?? []), duree].slice(-GARDEES);
    table[operation] = gardees;
    window.localStorage.setItem(MEMOIRE, JSON.stringify(table));
  } catch {
    /* idem : on perd la mémoire d'un soir, pas l'écran */
  }
}

export function reference(operation: string): number {
  const durees = historique()[operation] ?? [];
  if (durees.length === 0) return REFERENCE_PAR_DEFAUT;
  const triees = [...durees].sort((a, b) => a - b);
  const mediane = triees[Math.floor(triees.length / 2)] ?? REFERENCE_PAR_DEFAUT;
  return Math.min(REFERENCE_MAX, Math.max(REFERENCE_MIN, mediane));
}

/* --- L'avancement ---------------------------------------------------------- */

/**
 * La part d'un segment en vol.
 *
 * `1 − e^(−t/τ)` atteint 63 % à la durée de référence puis ralentit sans
 * s'arrêter : une opération deux fois plus longue que d'habitude continue
 * visiblement d'avancer, et rien ne se fige — ce qui est exactement la plainte
 * qu'on répare.
 */
function part(appel: EnVol, a: number): number {
  if (appel.mesure !== null) return PLAFOND * Math.min(1, Math.max(0, appel.mesure));
  const ecoule = Math.max(0, a - appel.debut);
  return PLAFOND * (1 - Math.exp(-ecoule / appel.reference));
}

function recalculer(): void {
  if (enVol.length === 0) return;
  const a = maintenant();
  const total = enVol.length + terminees;
  const somme = terminees + enVol.reduce((acc, appel) => acc + part(appel, a), 0);
  // **Jamais en arrière.** Une requête qui démarre pendant qu'une autre est en
  // vol fait baisser la moyenne, et une barre qui recule se lit comme une
  // panne — alors qu'il vient seulement de s'ajouter du travail.
  const calcule = somme / total;
  if (calcule > valeur) {
    valeur = calcule;
    prevenir();
  }
}

function battre(): void {
  if (minuterie !== null) return;
  // 80 ms : assez pour que l'œil voie bouger, assez peu pour ne rien coûter.
  minuterie = setInterval(recalculer, 80);
}

function arreterDeBattre(): void {
  if (minuterie === null) return;
  clearInterval(minuterie);
  minuterie = null;
}

/* --- Ce que `call()` appelle ---------------------------------------------- */

export function demarrer(operation: string): number {
  if (effacement !== null) {
    // Une nouvelle attente pendant que la précédente s'efface : on repart de
    // zéro plutôt que de reprendre une barre pleine, qui annoncerait la fin de
    // ce qui commence.
    clearTimeout(effacement);
    effacement = null;
    terminees = 0;
    valeur = 0;
  }
  const id = suivant++;
  enVol = [...enVol, { id, operation, debut: maintenant(), reference: reference(operation), mesure: null }];
  battre();
  recalculer();
  prevenir();
  return id;
}

/** L'avancement réellement mesuré d'un envoi, entre 0 et 1. Un seul appelant :
 *  l'envoi d'une photo vers le stockage (`photo/upload`). */
export function progresser(id: number, fraction: number): void {
  const appel = enVol.find((candidat) => candidat.id === id);
  if (!appel) return;
  appel.mesure = fraction;
  recalculer();
}

export function terminer(id: number): void {
  const appel = enVol.find((candidat) => candidat.id === id);
  if (!appel) return;
  retenir(appel.operation, maintenant() - appel.debut);
  enVol = enVol.filter((candidat) => candidat.id !== id);
  terminees += 1;

  if (enVol.length > 0) {
    recalculer();
    return;
  }

  arreterDeBattre();
  valeur = 1;
  prevenir();
  effacement = setTimeout(() => {
    effacement = null;
    terminees = 0;
    valeur = 0;
    prevenir();
  }, TENUE_PLEINE);
}

/* --- La lecture ------------------------------------------------------------ */

export function avancement(): number {
  return valeur;
}

function souscrire(abonne: () => void): () => void {
  abonnes.add(abonne);
  return () => {
    abonnes.delete(abonne);
  };
}

/** Zéro sur le serveur : il n'y a pas d'attente pendant un rendu de chaîne. */
const AU_SERVEUR = () => 0;

export function useAvancement(): number {
  return useSyncExternalStore(souscrire, avancement, AU_SERVEUR);
}

/** Pour les tests : remet le module dans l'état du premier chargement. */
export function _reinitialiser(): void {
  arreterDeBattre();
  if (effacement !== null) clearTimeout(effacement);
  effacement = null;
  enVol = [];
  terminees = 0;
  valeur = 0;
  abonnes.clear();
}
